import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The React binding for @athena/i18n.
 *
 * Two packages, on purpose. `@athena/i18n` holds the dictionaries and the
 * lookup, has no dependencies, and is what the API and the email templates
 * read. This is the twenty lines that make the same `t` available to a
 * component tree, and it lives in the design system because the design system
 * is the React layer every front end shares — the tutor and student app today,
 * `apps/admin` when it lands (CLAUDE.md). One dictionary, one `t`, one
 * provider: V1.md section 23 asks for exactly that, and a second mechanism for
 * Admin is the thing it rules out.
 *
 * Components here read `t` rather than taking every word as a prop. The
 * alternative was considered: the design system already takes most of its copy
 * as props (`emptyMessage`, `correctLabel`, `legend`), and that is right for
 * text a screen owns. But a `Badge`'s status words, an upload area's
 * instructions and "Not matched" belong to the component — they are the same
 * words on every screen — and threading two dozen new props through every call
 * site would put the design system's own vocabulary in the app's keeping.
 */
import { createTranslator, DEFAULT_LANGUAGE } from '@athena/i18n';
import { createContext, useContext, useEffect, useMemo } from 'react';
/**
 * Built once, at module load, for the case where there is no provider.
 *
 * That case is isolated rendering — a Storybook story, a component test — and
 * not the app, which mounts a provider above the router. It resolves in the
 * default language rather than throwing, because a component that cannot be
 * rendered without a provider is a component that cannot be tested without
 * one, and it renders real Spanish rather than keys so a story is still worth
 * looking at.
 */
const FALLBACK = createTranslator(DEFAULT_LANGUAGE);
const I18nContext = createContext(null);
export function I18nProvider({ language, children }) {
    const value = useMemo(() => ({ language, t: createTranslator(language) }), [language]);
    /**
     * The document's own language, kept in step.
     *
     * Not decoration: `lang` is what tells a screen reader which voice to use
     * and a browser which hyphenation and quotation rules apply, and a Spanish
     * page announced in an English voice is the accessibility defect this one
     * line prevents. It belongs here rather than in a route component because
     * this provider is the one thing that always knows the answer.
     */
    useEffect(() => {
        const root = globalThis.document?.documentElement;
        if (root)
            root.lang = language;
    }, [language]);
    return _jsx(I18nContext.Provider, { value: value, children: children });
}
/** `t('key')`, or `t('key', [a, b])`. See @athena/i18n for the contract. */
export function useTranslate() {
    return useContext(I18nContext)?.t ?? FALLBACK;
}
/** The language the tree is rendering in. For a link that has to name it. */
export function useLanguage() {
    return useContext(I18nContext)?.language ?? DEFAULT_LANGUAGE;
}
//# sourceMappingURL=i18n.js.map